import { recommendationsApi, type ResolveRecommendationInput } from '@/api/recommendations'
import type {
  AttributionEventType,
  Recommendation,
  SourceSurface,
  TargetSlot,
} from '@/types/recommendation'

const CACHE_PREFIX = 'zwy-recommendation:v1:'
const EVENT_PREFIX = 'zwy-recommendation-event:v1:'
const AUDIENCE_SKIP_KEY = 'zwy-recommendation-audience-skip:v1'

const storage = () => (typeof sessionStorage === 'undefined' ? null : sessionStorage)

const uuid = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  const values = new Uint8Array(16)
  globalThis.crypto?.getRandomValues?.(values)
  values[6] = (values[6] & 0x0f) | 0x40
  values[8] = (values[8] & 0x3f) | 0x80
  const hex = [...values].map((value) => value.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

export const recommendationCacheKey = (
  surface: SourceSurface,
  context: string | number | undefined,
  chapter?: string,
) => `${CACHE_PREFIX}${surface}:${String(context || 'general')}:${chapter || '*'}`

export const clearRecommendationCache = () => {
  const target = storage()
  if (!target) return
  Object.keys(target)
    .filter((key) => key.startsWith(CACHE_PREFIX))
    .forEach((key) => target.removeItem(key))
}

export const hasSkippedAudienceDisambiguation = () => storage()?.getItem(AUDIENCE_SKIP_KEY) === 'true'

export const skipAudienceDisambiguation = () => storage()?.setItem(AUDIENCE_SKIP_KEY, 'true')

export const clearAudienceDisambiguationSkip = () => storage()?.removeItem(AUDIENCE_SKIP_KEY)

export const cacheRecommendation = (
  recommendation: Recommendation | null,
  surface: SourceSurface,
  context?: string | number,
  chapter?: string,
) => {
  if (!recommendation) return null
  const target = storage()
  const key = recommendationCacheKey(surface, context, chapter)
  const cached = target?.getItem(key)
  if (cached) {
    try {
      const parsed = JSON.parse(cached) as Recommendation
      if (parsed.rid && parsed.recommendationId) return parsed
    } catch {
      target?.removeItem(key)
    }
  }
  target?.setItem(key, JSON.stringify(recommendation))
  return recommendation
}

export const resolveRecommendationCached = async (
  input: ResolveRecommendationInput,
  context?: string | number,
) => {
  const key = recommendationCacheKey(input.sourceSurface, context || input.questionBankId, input.chapter)
  const target = storage()
  const cached = target?.getItem(key)
  if (cached) {
    try {
      return JSON.parse(cached) as Recommendation
    } catch {
      target?.removeItem(key)
    }
  }
  const recommendation = await recommendationsApi.resolve(input)
  if (recommendation) target?.setItem(key, JSON.stringify(recommendation))
  return recommendation
}

export const resolvePublicRecommendationCached = async (sourceSurface: SourceSurface) => {
  const key = recommendationCacheKey(sourceSurface, 'brand')
  const target = storage()
  const cached = target?.getItem(key)
  if (cached) {
    try {
      return JSON.parse(cached) as Recommendation
    } catch {
      target?.removeItem(key)
    }
  }
  const recommendation = await recommendationsApi.resolvePublic({ sourceSurface })
  if (recommendation) target?.setItem(key, JSON.stringify(recommendation))
  return recommendation
}

export const recordRecommendationEvent = async (
  recommendation: Recommendation,
  eventType: AttributionEventType,
  targetSlot: TargetSlot,
) => {
  const key = `${EVENT_PREFIX}${recommendation.rid}:${eventType}:${targetSlot}`
  const target = storage()
  const eventId = target?.getItem(key) || uuid()
  target?.setItem(key, eventId)
  return recommendationsApi.recordEvent({
    eventId,
    rid: recommendation.rid,
    eventType,
    targetSlot,
  })
}

export const recordLearningProtectionEvent = async (
  recommendation: Recommendation | undefined,
  eventType: Extract<AttributionEventType, 'quizRestart' | 'wrongQuestionReview'>,
) => {
  if (!recommendation) return
  try {
    await recordRecommendationEvent(recommendation, eventType, 'unit')
  } catch (error) {
    console.warn('学习保护指标记录失败，不影响学习动作', error)
  }
}
